import { useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import type { TeamMemberProfileDto } from '../../../../domain/contracts/api.contracts';
import { useTranslation } from '../../../../application/hooks/useTranslation';
import { AmountDisplay } from '../../../components/ui/AmountDisplay';
import { isAgent, telHref } from '../teamFormat';

function PhoneItem({ phone }: { phone: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(phone).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <a href={telHref(phone)} className="tabular">{phone}</a>
      <button
        type="button"
        onClick={copy}
        title="Copy phone"
        aria-label="Copy phone"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'inline-flex',
          color: 'var(--color-text-muted)',
        }}
        data-testid="profile-copy-phone"
      >
        {copied ? <Check size={14} color="#059669" /> : <Copy size={14} />}
      </button>
    </span>
  );
}

export function ProfileTab({ profile, onOpenMember }: { profile: TeamMemberProfileDto; onOpenMember: (userId: string) => void }) {
  const { t } = useTranslation();
  const { card, sponsor, sponsorInScope, path, subscriptions } = profile;

  const rows: Array<[string, ReactNode]> = [
    [t('team.accountId'), <span className="tabular">{card.apartmentId}</span>],
    [t('team.phone'), <PhoneItem phone={card.phone} />],
    [
      t('team.sponsor'),
      !sponsor ? '—' : sponsorInScope ? (
        <button type="button" className="m360-link" onClick={() => onOpenMember(sponsor.apartmentId)} data-testid="member360-sponsor">
          {sponsor.fullName} ({sponsor.tier})
        </button>
      ) : (
        <span>
          {sponsor.fullName} ({t('team.you')})
        </span>
      ),
    ],
    [
      t('team.path'),
      <span className="m360-path">
        {path.map((p, i) => (i === 0 ? t('team.you') : `${p.fullName} (${p.tier})`)).join(' › ')}
      </span>,
    ],
  ];
  if (isAgent(card.tier)) {
    // Only L1 subscribers hold subscriptions; agents (L2–L6) show team metrics instead.
    rows.push(
      [t('team.directReports'), <span className="tabular">{card.directReportsCount}</span>],
      [t('team.total'), <span className="tabular">{card.totalTeamCount}</span>],
      [t('team.teamVolume'), <AmountDisplay amount={card.activeVolumeBDT} />],
    );
  } else {
    rows.push(
      [t('team.apartment'), subscriptions.map((s) => s.subscriptionId).join(', ') || '—'],
    );
  }

  return (
    <dl className="m360-dl" data-testid="member360-profile">
      {rows.map(([label, value]) => (
        <div key={label} className="m360-dl-row">
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
